import {
  deleteAnomaly,
  openDoor,
  useAiVideoUrl,
  useAnomalies,
  useBoutiques,
  useCheckinKiosks,
  useCheckoutKiosks,
  useLiveAiArticles,
  useLiveAnomalies,
  usePreAuthorizations,
  useStoreOpeningState,
  useVideoIds,
} from '@gozoki/api'
import type { Kiosk as KioskType, UniqueArticle } from '@gozoki/api-types'
import { Button } from '@mui/material'
import Box from '@mui/material/Box'
import { useMutation } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { LockOpen } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import { z } from 'zod/v3'
import '../../assets/styles/CameraFont.module.css'
import InterventionButtons from '../../components/buttons/Intervention.tsx'
import { useToast } from '../../components/communication/Toast.tsx'
import Page from '../../components/Page.tsx'
import AnomalyCards from '../../components/routes/cameras/AnomalyCards.tsx'
import CameraWindow from '../../components/routes/cameras/CameraWindow.tsx'
import PreAuthorizationCards from '../../components/routes/cameras/PreAuthorizationCards.tsx'
import { StoresPicker } from '../../utils/dashboard/storesPicker.tsx'
import { usePreAuthorizationNotifications } from '../../utils/websockets/usePreAuthorizationNotifications.ts'

export const Route = createFileRoute('/_authenticated/cameras')({
  component: CamerasPages,
  validateSearch: z.object({
    storeId: z.number().int().positive().optional(),
  }),
})

function CamerasPages() {
  const navigate = Route.useNavigate()
  const storeId = Route.useSearch().storeId?.toString() ?? '3'

  const [centralVideoId, setCentralVideoId] = useState<string | null>(null)

  const [Toast, showToast] = useToast()

  const { data: boutiques } = useBoutiques()
  const { data: videoIds } = useVideoIds(storeId)
  const { data: checkinKiosks } = useCheckinKiosks(Number.parseInt(storeId, 10))
  const { data: checkoutKiosks } = useCheckoutKiosks(Number.parseInt(storeId, 10))
  const { data: storeOpeningState } = useStoreOpeningState(Number.parseInt(storeId, 10))

  const isAttendedMode = storeOpeningState?.openingMode === 'ATTENDED'

  const checkinKioskId = checkinKiosks?.length ? checkinKiosks[0].id : null

  const { data: anomalies } = useAnomalies(storeId)
  useLiveAnomalies(storeId)
  useLiveAnomalies(storeId)
  useLiveAiArticles(storeId)

  const { data: preAuthorizations, refetch: refetchPreAuthorizations } = usePreAuthorizations(
    Number(storeId),
  )

  const handleNewPreAuthorization = useCallback(
    (data: { storeId?: number }) => {
      if (data.storeId === Number(storeId)) {
        refetchPreAuthorizations()
      }
    },
    [storeId, refetchPreAuthorizations],
  )

  usePreAuthorizationNotifications(handleNewPreAuthorization)

  const { data: mainVideoUrl } = useAiVideoUrl(storeId, centralVideoId)

  const { mutate: mutateDeleteAnomaly } = useMutation({
    mutationFn: ({ sId, aId }: { sId: string; aId: string }) => deleteAnomaly(sId, aId),
    onError: () => {
      showToast({
        message: "Une erreur est survenue lors de la suppression de l'anomalie",
        severity: 'error',
      })
    },
  })

  const handleDeleteAnomaly = (anomalyId: string) => {
    mutateDeleteAnomaly({ aId: anomalyId, sId: storeId })
  }

  const handleVideoClick = (videoId: string) => {
    if (videoId !== centralVideoId) {
      setCentralVideoId(videoId)
    }
  }

  useEffect(() => {
    if (videoIds && videoIds.length > 0) {
      setCentralVideoId(videoIds[0])
    }
  }, [videoIds])

  const handleChangeStore = (sId: number) => navigate({ to: '/cameras', search: { storeId: sId } })

  return (
    <Page>
      <div style={{ marginLeft: '25px' }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-start',
            alignItems: 'flex-start',
            gap: '20px',
            // MarginTop: '10px',
            height: '100%',
          }}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '10px',
              height: '100%',
              justifyContent: 'center',
              // Flex: 3,
            }}
          >
            <div
              style={{
                color: 'darkgreen',
                fontFamily: 'BlockBerthold_regular',
                fontSize: '40px',
                fontWeight: '400',
                // Display: 'flex',
              }}
            >
              Caméras
            </div>
            <div style={{ display: 'flex', gap: '16px' }}>
              <div style={{ flex: 1 }}>
                <InterventionButtons
                  checkType="CHECK-IN"
                  kioskId={checkinKioskId}
                  storeId={Number(storeId)}
                />
              </div>
              <div style={{ flex: 1 }}>
                {checkinKioskId && (
                  <div style={{ height: '92px', marginTop: '55px' }}>
                    <Button
                      variant="contained"
                      sx={{
                        '&:hover': { backgroundColor: 'darkorange' },
                        backgroundColor: 'orange',
                        height: '100%',
                        width: '100%',
                      }}
                      onClick={() => openDoor(checkinKioskId, true)}
                      disabled={!isAttendedMode}
                    >
                      <Box
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                        justifyContent="center"
                        gap="8px"
                        style={{
                          color: '#3E2500',
                          font: 'Avenir Next',
                        }}
                      >
                        <span
                          style={{
                            alignItems: 'center',
                            display: 'flex',
                            gap: '8px',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          <LockOpen style={{ height: 24, width: 24 }} />
                          Ouvrir la porte
                        </span>
                      </Box>
                    </Button>
                  </div>
                )}
              </div>
            </div>

            <div
              style={{
                maxHeight: '80vh',
                maxWidth: '80vh',
                position: 'relative',
              }}
            >
              <div style={{ aspectRatio: 1.52, maxHeight: '80vh', maxWidth: '80vh' }}>
                <img
                  alt={`camera ${centralVideoId}`}
                  src={mainVideoUrl}
                  style={{
                    aspectRatio: 1.5,
                    border: '8px solid #FF9900',
                    borderRadius: '16px',
                    height: '100%',
                    width: '100%',
                  }}
                />
              </div>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  gap: '21px',
                  // MaxHeight: '80vh',
                  // MaxWidth: '100vh',
                  overflowX: 'auto',
                }}
              >
                {videoIds?.map((videoId) => (
                  <CameraWindow
                    key={videoId}
                    videoId={videoId}
                    storeId={storeId}
                    onClick={() => handleVideoClick(videoId)}
                    isSelected={centralVideoId === videoId}
                  />
                ))}
              </div>
            </div>
          </div>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '20px',
              justifyContent: 'center',
            }}
          >
            <Toast />
            <Box sx={{ alignItems: 'center', display: 'flex', justifyContent: 'center' }} py={2}>
              <Box sx={{ width: 500 }}>
                <StoresPicker
                  onlyBoutiques
                  defaultStore={Number.parseInt(storeId, 10)}
                  setUniqueStore={handleChangeStore}
                  usedStores={boutiques?.map((s) => s.id)}
                />
              </Box>
            </Box>
            {checkoutKiosks?.length ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {checkoutKiosks.map((kiosk) => (
                  <KioskPanel key={kiosk.id} kiosk={kiosk} storeId={Number(storeId)} />
                ))}
              </div>
            ) : null}

            <div style={{ height: '80%', overflowY: 'auto' }}>
              <AnomalyCards anomalies={anomalies} onDelete={handleDeleteAnomaly} />
            </div>
            <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
              <PreAuthorizationCards
                preAuthorizations={preAuthorizations ?? []}
                storeId={Number(storeId)}
                showToast={showToast}
              />
            </div>
            <div style={{ flexGrow: 1 }} />
          </div>
        </div>
      </div>
    </Page>
  )
}

interface KioskPanelProps {
  kiosk: KioskType
  storeId: number
}

const KioskPanel = ({ kiosk, storeId }: KioskPanelProps) => {
  const { cart } = kiosk

  return (
    <div
      style={{
        border: '1px solid #D9D9D9',
        borderRadius: '12px',
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        padding: '16px',
      }}
    >
      <div
        style={{
          fontFamily: 'Avenir Next',
          fontSize: '18px',
          fontWeight: 600,
        }}
      >
        {kiosk.label}
      </div>

      <InterventionButtons checkType="" kioskId={kiosk.id} storeId={storeId} />

      {cart && cart.articles && cart.articles.length > 0 && (
        <div>
          <div
            style={{
              color: '#2E7D32',
              fontFamily: 'Avenir Next',
              fontSize: '16px',
              fontWeight: 600,
              marginBottom: '8px',
            }}
          >
            Panier en cours ({cart.articles.length} articles)
          </div>
          <ul
            style={{
              fontFamily: 'Avenir Next',
              fontSize: '14px',
              lineHeight: '1.4',
              margin: 0,
              maxHeight: '150px',
              overflowY: 'auto',
              paddingLeft: '16px',
            }}
          >
            {cart.articles.map((article: UniqueArticle) => (
              <li
                key={article.id}
                style={{
                  color: '#333',
                  marginBottom: '4px',
                }}
              >
                {article.product?.label ?? 'Article'}&nbsp;&nbsp;(
                {article.product.price}€)
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}
